import { Box, Typography, Card, Stack, Skeleton } from '@mui/material';
import { ArticleCardVariant } from './CardUtils';
import { defaultCoverImageAspectRatio } from '../../types/types';

function CategoryListSkeleton() {
  return (
    <Typography variant='caption'>
      <Stack
        direction='row'
        spacing={1}
        sx={{
          paddingY: 1.5,
        }}
      >
        <Skeleton width='90%' />
      </Stack>
    </Typography>
  );
}

function HighlightedArticleCardSkeleton() {
  return (
    <Card
      sx={{
        paddingX: 2,
        paddingBottom: 0.25,
        paddingTop: 0,
        height: '100%',
      }}
      variant='outlined'
    >
      <Box
        sx={{
          marginX: -2,
          marginBottom: 2,
        }}
      >
        <Skeleton
          variant='rectangular'
          sx={{ aspectRatio: defaultCoverImageAspectRatio }}
          width={'100%'}
          height={'100%'}
        />
      </Box>
      {/* Overline section */}
      <Box
        sx={{
          marginBottom: 0.5,
        }}
      >
        <Typography variant='overline'>
          <Skeleton width='90%' />
        </Typography>
      </Box>

      {/* Main content area */}
      <Skeleton width='100%' height='60px' />

      {/* Article card footer */}
      <Box>
        <Box
          sx={{
            paddingY: 1.5,
          }}
        >
          <Typography variant='subtitle2'>
            <Skeleton width='90%' />
          </Typography>
        </Box>
        <CategoryListSkeleton />
      </Box>
    </Card>
  );
}

interface ArticleCardProps {
  readonly variant?: ArticleCardVariant;
  readonly hideCoverImage?: boolean;
}

export default function ArticleCardSkeleton({
  variant = 'default',
  hideCoverImage = false,
}: ArticleCardProps) {
  if (variant === 'highlight') {
    return <HighlightedArticleCardSkeleton />;
  }

  let imageSkeletonSizeProps = {
    width: '148px',
    height: 'auto',
    sx: {
      aspectRatio: defaultCoverImageAspectRatio,
    },
  };

  if (variant === 'mobile') {
    imageSkeletonSizeProps = {
      width: '80px',
      height: 'auto',
      sx: {
        aspectRatio: defaultCoverImageAspectRatio,
      },
    };
  }

  return (
    <Card
      variant='outlined'
      data-testid='article-card-skeleton'
      sx={{
        paddingX: 2,
        paddingTop: 6,
        paddingBottom: 2,
        height: '100%',
      }}
    >
      <Stack
        direction='row'
        useFlexGap
        sx={{
          gap: 3,
        }}
      >
        <Stack
          direction='column'
          useFlexGap
          sx={{
            gap: 3,
            width: '100%',
          }}
        >
          <Skeleton variant='rectangular' width='100%' height='48px' />
          <Skeleton variant='rectangular' width='100%' height='24px' />
          <Skeleton variant='rectangular' width='100%' height='24px' />
        </Stack>
        {!hideCoverImage && (
          <Box>
            <Skeleton variant='rectangular' {...imageSkeletonSizeProps} />
          </Box>
        )}
      </Stack>
    </Card>
  );
}
